// useOffline — one hook that watches the connection and the two queues.
//
//   { online, pendingCount, failed, replayNow, dismissFailed }
//
// `owner` is the signed-in user's id. It is pushed into both queues before
// anything replays, so a queued op only ever goes to the server under the
// person who created it — see offline/owner.js for the payroll bug that is.
//
// Replays on: mount (leftovers from last session), the `online` event,
// the tab becoming visible, and window focus. Ops first, then photos. Both
// queues serialise their own replays, so overlapping triggers are harmless.
//
// Mounted ONCE, by FieldApp's provider; screens read the value from context
// rather than each running their own listeners.

import { useCallback, useEffect, useRef, useState } from 'react';
import { getQueue } from './queue';
import { getPhotoQueue } from './photos';

export function isOnline() {
  try { return typeof navigator === 'undefined' || navigator.onLine !== false; } catch (_) { return true; }
}

export function useOffline({ queue, photos, onReplayed, owner } = {}) {
  const q = queue || getQueue();
  const pq = photos || getPhotoQueue();
  const [online, setOnline] = useState(isOnline);
  const [, bump] = useState(0);
  const replayedRef = useRef(onReplayed);
  replayedRef.current = onReplayed;

  // Whose work is this, before anything is sent.
  //
  // What makes this safe is NOT the order of the two effects — measured: with
  // this one moved below the replay effect, every case in owner.test.js,
  // useOffline.test.js and Today.test.js still passes. It is that every effect
  // in a commit runs synchronously before any microtask, while `replay` filters
  // only after `await ready`. So `setOwner` has always landed by the time the
  // mount replay decides what it may send, whichever effect is declared first.
  //
  // Said plainly because the ordering LOOKS load-bearing and is not: nobody
  // should be afraid to move these, and nobody should trust an ordering the
  // suite does not enforce.
  //
  // Adoption waits on `ready` because the queue loads from IndexedDB
  // asynchronously and `adoptOrphans` reads the in-memory mirror.
  useEffect(() => {
    const id = owner === undefined || owner === null || owner === '' ? null : owner;
    q.setOwner(id);
    pq.setOwner(id);
    if (id === null) return;
    Promise.resolve(q.ready).then(() => q.adoptOrphans()).catch(() => {});
    Promise.resolve(pq.ready).then(() => pq.adoptOrphans()).catch(() => {});
  }, [q, pq, owner]);

  const replayNow = useCallback(async () => {
    if (!isOnline()) return { offline: true, sent: 0, failed: 0 };
    const a = await q.replay();
    const b = a.offline ? { sent: 0, failed: 0, offline: true } : await pq.replay();
    const out = { sent: a.sent + b.sent, failed: a.failed + b.failed, offline: !!(a.offline || b.offline) };
    if (out.sent > 0 && replayedRef.current) replayedRef.current(out);
    return out;
  }, [q, pq]);

  useEffect(() => {
    const rerender = () => bump((n) => n + 1);
    const unsubs = [q.subscribe(rerender), pq.subscribe(rerender)];
    const goOnline = () => { setOnline(true); replayNow(); };
    const goOffline = () => setOnline(false);
    const onVisible = () => { if (document.visibilityState === 'visible') { setOnline(isOnline()); replayNow(); } };
    const onFocus = () => { setOnline(isOnline()); replayNow(); };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    replayNow();
    return () => {
      unsubs.forEach((u) => u());
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [q, pq, replayNow]);

  const dismissFailed = useCallback((key) => {
    if (key === undefined) { q.dismissFailed(); pq.dismissFailed(); return; }
    if (q.get(key)) q.dismissFailed(key); else pq.dismissFailed(key);
  }, [q, pq]);

  const failed = [
    ...q.failed().map((r) => ({ key: r.key, op: r.op, error: r.error })),
    ...pq.failed().map((r) => ({ key: r.key, op: 'photo', error: r.error })),
  ];

  return {
    online,
    pendingCount: q.pending().length + pq.pending().length,
    failed,
    replayNow,
    dismissFailed,
    queue: q,
    photos: pq,
  };
}
